import { useAuth0 } from '@auth0/auth0-react';
import {
  Skeleton,
  Text,
  Divider,
  Flex,
  HStack,
  Link,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spacer,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  useRedirectAfterLogin,
  useUserAuthUnsafe,
  LoginPageContainer,
} from '~/contexts/UserAuthProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';
import mainLogo from '~/packages/common-frontend/images/Logo-Horizontal-Normal.svg';

// Fragments
const Auth0LoginButton = () => {
  const { loginWithRedirect } = useAuth0();
  return (
    <Button
      mt="24px"
      width="full"
      variant="secondary"
      colorScheme="v2-aurora"
      onClick={() => loginWithRedirect()}
    >
      Sign in with SSO
    </Button>
  );
};

const ForgotPasswordPrompt = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <Modal closeOnEsc isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Forgot password?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Contact your Customer Service Manager via{' '}
              <Link href="mailto:support@finleycms.com" color="blue.400">
                email
              </Link>{' '}
              to update your password.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" colorScheme="v2-aurora" onClick={onClose}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <HStack pt={4}>
        <Spacer />
        <Button
          fontWeight="light"
          fontSize="sm"
          variant="link"
          color="gray"
          onClick={onOpen}
        >
          Forgot password?
        </Button>
      </HStack>
    </>
  );
};

const ScheduleDemoPrompt = () => (
  <Text fontSize="xs">
    Don&apos;t have an account?{' '}
    <Link href="https://www.finleycms.com/contact" color="teal">
      Schedule a demo
    </Link>
  </Text>
);

const RouteBase = () => {
  const { status } = useUserAuthUnsafe();

  // Redirect logged in users to either:
  // - where the `?redirectAfterLogin` query param points to, or
  // - the homepage `/` route
  useRedirectAfterLogin(status);

  if (status === 'loading') return <Skeleton />;
  if (status === 'error') return <DefaultErrorRoute />;
  if (status === 'logged-in') return <Skeleton />;

  return (
    <LoginPageContainer logoSrc={mainLogo}>
      <Flex direction="column" gap="1rem">
        <ForgotPasswordPrompt />
        <HStack width="full" mt="1rem">
          <Divider />
          <Text px={1}>or</Text>
          <Divider />
        </HStack>
        <Auth0LoginButton />
        <ScheduleDemoPrompt />
      </Flex>
    </LoginPageContainer>
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      resetAfterCatch={false}
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <DefaultErrorRoute />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
